import { ChevronLineIcon } from "@/Icons/v2/ChevronLine";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import {
  FILE_STATUS_PARAMS,
  FileStatus,
  type LibraryFile,
} from "@/pages/library_v2/components/filesView/types";
import { useTrans } from "@/translations";

import { useCallback, useContext } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import type { FILE_LOCATION_TYPE } from "server/bucketApi/library/constants.js";

type Props = {
  file: LibraryFile;
};

export const FileStatusSelect: React.FC<Props> = ({ file }) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsState();
  const { setGlobalAlert } = useGlobalAlert();
  const { refetchFiles } = useContext(LibraryPageContext);

  const { mutate: moveFile } = trpc.library.moveFile.useMutation({
    onSuccess: () => {
      refetchFiles();
      setGlobalAlert({
        type: "success",
        message: "library:fileStatusChangeSuccessful",
      });
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentStatusChangeFailed",
      });
      console.error("Error changing document status:", error);
    },
  });

  const onChangeStatus = useCallback(
    async (_status: FileStatus) => {
      if (_status !== file.status) {
        await moveFile({
          terminalId: currentTerminal?.id!,
          filename: `${file.name}.${file.format}`,
          folderNameFrom:
            file.status === FileStatus.PUBLISHED ? file.folderName : "",
          from: file.status as string as FILE_LOCATION_TYPE,
          to: _status as string as FILE_LOCATION_TYPE,
        });
      }
    },
    [moveFile, file, currentTerminal],
  );

  return (
    <Popover>
      <PopoverButton className="inline-flex w-fit cursor-pointer items-center gap-[7px] bg-transparent">
        {file.status === FileStatus.PUBLISHED ? (
          <span className="inline-block rounded-[4px] border border-[#D2E5C4] bg-[#E8F2DE] px-2 py-[3px] text-[12px] font-semibold text-[#3F6B2B]">
            {t(FILE_STATUS_PARAMS[file.status].statusNameKey)}
          </span>
        ) : (
          <span className="text-[13px] font-semibold text-[#B73A23]">
            {t(FILE_STATUS_PARAMS[file.status].statusNameKey)}
          </span>
        )}
        <ChevronLineIcon />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: "bottom start", gap: 6 }}
        className="border-grey3 z-[10000] w-[196px] rounded-lg border bg-white p-[5px] shadow-lg"
      >
        {({ close }) => (
          <>
            {Object.keys(FILE_STATUS_PARAMS).map((statusKey) => (
              <button
                key={statusKey}
                type="button"
                onClick={() => {
                  onChangeStatus(statusKey as FileStatus);
                  close();
                }}
                className="hover:bg-grey1 block w-full cursor-pointer rounded-[5px] px-[10px] py-2 text-left text-[13px] text-[#1D1C1C]"
              >
                {t(FILE_STATUS_PARAMS[statusKey as FileStatus].statusNameKey)}
              </button>
            ))}
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
